import { isImpersonating } from '../impersonationView';
import { canInviteTeamMembers } from './instructorMeterLimits';

/** StudyAK internal operator (platform super admin). */
export function isSuperAdmin(userType) {
  return String(userType) === '2';
}

/**
 * Resolves org role from live entitlements or cached login payload.
 * @returns {'owner'|'admin'|'instructor'|null}
 */
export function resolveOrgRole(entitlements) {
  if (
    entitlements?.role === 'owner'
    || entitlements?.role === 'admin'
    || entitlements?.role === 'instructor'
  ) {
    return entitlements.role;
  }
  // While viewing as another user, never inherit the prior session's cached role.
  if (isImpersonating()) {
    return null;
  }
  const cached = localStorage.getItem('instructorRole');
  if (cached === 'owner' || cached === 'admin' || cached === 'instructor') {
    return cached;
  }
  return null;
}

/**
 * Organization admin-or-higher (owner or admin) — can manage team and org settings.
 * (Billing is owner-only and gated separately via entitlements.ui.show_billing.)
 */
export function isOrgAdmin(entitlements) {
  const role = resolveOrgRole(entitlements);
  return role === 'owner' || role === 'admin';
}

/** Organization instructor — teaching surfaces only; no Admin/Billing. */
export function isOrgInstructor(entitlements) {
  return resolveOrgRole(entitlements) === 'instructor';
}

/** System menu + super-admin-only routes (not while impersonating a customer). */
export function canAccessSystem(userType) {
  return isSuperAdmin(userType) && !isImpersonating();
}

/**
 * The signed-in operator is a platform super admin viewing their OWN console
 * (not impersonating a customer). Super admins get the full management surface
 * — Admin, Billing, System — regardless of any org role, mirroring canAccessSystem.
 */
function isSuperAdminOwnView() {
  return isSuperAdmin(localStorage.getItem('userType')) && !isImpersonating();
}

/** Admin menu + org management routes. */
export function canAccessAdmin(entitlements) {
  return isSuperAdminOwnView() || isOrgAdmin(entitlements);
}

/** Billing menu — the org owner or a platform super admin (billing hidden for others). */
export function canAccessBilling(entitlements) {
  if (isSuperAdminOwnView()) {
    return true;
  }
  if (!isOrgAdmin(entitlements)) {
    return false;
  }
  return entitlements?.ui?.show_billing !== false;
}

/**
 * Non-owner org staff (admin or instructor) — positively identified by role.
 * They cannot renew billing themselves, so on plan expiry they get a read-only
 * "ask your owner" notice instead of the owner's plan-selection dialog. A null /
 * unknown role deliberately returns false so we never show them a misleading
 * "contact your owner" message.
 */
export function isNonOwnerOrgStaff(entitlements) {
  const role = resolveOrgRole(entitlements);
  return role === 'admin' || role === 'instructor';
}

/** Training & Compliance super-admin section. */
export function canAccessTrainingCompliance(userType) {
  return isSuperAdmin(userType) && !isImpersonating();
}

/** Super admin — global user search (any instructor account). */
export function canImpersonateGlobally(userType) {
  return isSuperAdmin(userType) && !isImpersonating();
}

/**
 * Org owner/admin — view-as limited to members of their organization, and only on the
 * Organization tier or above. (Backend restricts targets by role: owner → admins +
 * instructors; admin → instructors only.)
 */
export function canImpersonateOrgMembers(userType, entitlements) {
  if (isImpersonating()) {
    return false;
  }
  if (isSuperAdmin(userType)) {
    return false;
  }
  return isOrgAdmin(entitlements)
    && Boolean(entitlements?.orgId)
    && canInviteTeamMembers(entitlements);
}

/**
 * True when the impersonation search should render LOCKED: an org owner/admin who would
 * have view-as, but is on a plan below the Organization tier. Distinct from having no
 * access at all (instructors, or super admins who use the global search).
 */
export function isImpersonationSearchLocked(userType, entitlements) {
  if (isImpersonating() || isSuperAdmin(userType)) {
    return false;
  }
  return isOrgAdmin(entitlements)
    && Boolean(entitlements?.orgId)
    && !canInviteTeamMembers(entitlements);
}

/** Sidebar impersonation search bar (global or org-scoped). */
export function canUseImpersonationSearch(userType, entitlements) {
  return canImpersonateGlobally(userType) || canImpersonateOrgMembers(userType, entitlements);
}

/** Platform-wide user search — super admin only, never while viewing as another user. */
export function canUseGlobalUserSearch(userType) {
  return isSuperAdmin(userType) && !isImpersonating();
}
